/**
 * One key per intent, for requests the server must not act on twice.
 *
 * A key belongs to what is being sent: the same body sent again — a second tap,
 * a retry after a failure — carries the same key, so the server does it once and
 * answers the repeat with the first result. A different body is a different
 * intent and gets a new key. After a success the caller resets, so sending the
 * same thing again on purpose is a new intent too.
 */

import { useState } from 'react';

export interface IntentKeys {
  keyFor(body: unknown): string;
  reset(): void;
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

/** Exported for its test; screens use {@link useIdempotencyKey}. */
export function intentKeys(make: () => string = newKey): IntentKeys {
  let body: string | null = null;
  let key = '';
  return {
    keyFor(next) {
      const serialized = JSON.stringify(next);
      if (serialized !== body) {
        body = serialized;
        key = make();
      }
      return key;
    },
    reset() {
      body = null;
    },
  };
}

export function useIdempotencyKey(): IntentKeys {
  // Created once per mounted screen; the object itself holds the changing key.
  const [keys] = useState(() => intentKeys());
  return keys;
}
